// LivestreamTokenFetcher.jsx
import { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import fetchStreamsToken from '../action/fetchStreamsToken.js';
import getGoalData from '../selector/getGoalData.js';
import getLiveStreamData from '../selector/getLiveStreamData.js';
import getMeData from '../selector/getMeData.js';
import { getHasStreamToken } from '../selector/getLivestreamComputedData.js';
import getStreamTokenDecodedData from '../selector/getStreamTokenDecodedData.js';
import getTimestampOffset, {
  Accuracy,
} from '../selector/getTimestampOffset.js';
import usePreviousValue from '../hook/usePreviousValue.js';
import getCurrentUnixTimestamp from '../resource/getCurrentUnixTimestamp.js';
import { PRESET_SD } from '../resource/liveStreamConstants.js';
import { livestream as livestreamDebug } from '../resource/debug.js';

const log = livestreamDebug.extend('log:LivestreamTokenFetcher');

export const LivestreamTokenFetcher = ({ streamId = '' }) => {
  const dispatch = useDispatch();
  const meId = useSelector(state => getMeData(state, 'id'));
  const streamStatus = useSelector(state =>
    streamId ? getLiveStreamData(state, [streamId], 'status') : undefined
  );
  const hasStreamToken = useSelector(state =>
    streamId ? getHasStreamToken(state, streamId) : false
  );
  const tokenExp = useSelector(state =>
    streamId ? getStreamTokenDecodedData(state, streamId)?.exp : undefined
  );
  const timestampOffsetSeconds = useSelector(state =>
    getTimestampOffset(state, Accuracy.SECOND)
  );
  const fundingId = useSelector(state =>
    streamId
      ? getLiveStreamData(state, [streamId, 'privateShow'], 'fundingId')
      : undefined
  );
  const hasMeTicket = useSelector(state => {
    if (!fundingId || !meId) return false;
    return (
      (getGoalData(state, [fundingId, 'breakdown', meId], 'progress') || 0) > 0
    );
  });
  const prevHasMeTicket = usePreviousValue(hasMeTicket);

  useEffect(() => {
    const nowSeconds = getCurrentUnixTimestamp({
      offsetSeconds: timestampOffsetSeconds || 0,
    });
    const isTokenValid = hasStreamToken && tokenExp > nowSeconds;
    const justGainedTicket = !prevHasMeTicket && hasMeTicket;
    const shouldFetchStreamsToken =
      !!streamId &&
      !!meId &&
      streamStatus === PRESET_SD &&
      (!isTokenValid || justGainedTicket);
    const logData = {
      streamId,
      meId,
      streamStatus,
      hasStreamToken,
      tokenExp,
      nowSeconds,
      isTokenValid,
      prevHasMeTicket,
      hasMeTicket,
      justGainedTicket,
      shouldFetchStreamsToken,
    };
    log('evaluate fetch stream token', logData);
    if (shouldFetchStreamsToken) {
      log('dispatch fetchStreamsToken', logData);
      dispatch(fetchStreamsToken({ streamId, jitterFactor: 3 }));
    }
  }, [
    streamId,
    meId,
    streamStatus,
    hasStreamToken,
    tokenExp,
    timestampOffsetSeconds,
    prevHasMeTicket,
    hasMeTicket,
    dispatch,
  ]);

  return null;
};

LivestreamTokenFetcher.propTypes = {
  streamId: PropTypes.string,
};

export default LivestreamTokenFetcher;
